import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { CHATBOT_CONFIG } from '../../config';
import { chatBotService } from '../../services/ChatBotService';
import { type ChatBotState, type ResolvedChatbotInit, type SurveyState } from '../../types';
import {
  loadChatBotOpenState,
  loadChatSessionReadyState,
  loadMessages,
  saveChatBotOpenState,
  saveChatSessionReadyState,
  saveMessages,
} from '../../utils/storage';
import { resolveChatbotInit } from '../../utils/initResolver';

interface UseChatLifecycleParams {
  authLocale: string | undefined;
  setSurveyState: Dispatch<SetStateAction<SurveyState>>;
}

interface UseChatLifecycleResult {
  state: ChatBotState;
  setState: Dispatch<SetStateAction<ChatBotState>>;
  initState: ResolvedChatbotInit;
  setInitState: Dispatch<SetStateAction<ResolvedChatbotInit>>;
  isInitLoaded: boolean;
  isSessionReadyRef: MutableRefObject<boolean>;
  resetSession: (options?: { closeWidget?: boolean; resetSurvey?: boolean }) => void;
}

export const useChatLifecycle = ({ authLocale, setSurveyState }: UseChatLifecycleParams): UseChatLifecycleResult => {
  const isSessionReadyRef = useRef(loadChatSessionReadyState());

  const [state, setState] = useState<ChatBotState>({
    isOpen: CHATBOT_CONFIG.autoOpen || loadChatBotOpenState(),
    messages: [],
    isLoading: false,
    isResponding: false,
    unreadCount: 0,
  });
  const [initState, setInitState] = useState<ResolvedChatbotInit>(resolveChatbotInit(null));
  const [isInitLoaded, setIsInitLoaded] = useState(false);

  const buildInitialMessages = useCallback((resolvedInit: ResolvedChatbotInit, onlyGreeting = false): ChatBotState['messages'] => {
    const localizedGreetingHtml = resolvedInit.greetingHtml;
    const localizedDisclaimerHtml = resolvedInit.disclaimerHtml;

    const initialMessages: ChatBotState['messages'] = [];

    if (localizedGreetingHtml) {
      initialMessages.push(chatBotService.createMessage('assistant', localizedGreetingHtml, 'sent', { contentFormat: 'html' }));
    }

    if (!onlyGreeting) {
      if (localizedDisclaimerHtml && localizedDisclaimerHtml !== localizedGreetingHtml) {
        initialMessages.push(chatBotService.createMessage('assistant', localizedDisclaimerHtml, 'sent', { contentFormat: 'html' }));
      }
    }

    return initialMessages;
  }, []);

  const resetSession = useCallback((options?: { closeWidget?: boolean; resetSurvey?: boolean }) => {
    if (isSessionReadyRef.current) {
      void chatBotService.closeSession();
    }
    isSessionReadyRef.current = false;
    saveChatSessionReadyState(false);
    const initialMessages = buildInitialMessages(initState, true);
    setState(prev => ({
      ...prev,
      messages: initialMessages,
      unreadCount: 0,
      ...(options?.closeWidget && { isOpen: false }),
    }));
    if (options?.resetSurvey) {
      setSurveyState({ feedbackSurveyID: null, questions: [], scores: {}, comments: '' });
    }
  }, [buildInitialMessages, initState, setSurveyState]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialMessages = async () => {
      const initData = await chatBotService.getInitData();
      if (!isMounted) {
        return;
      }

      const resolvedInit = resolveChatbotInit(initData);
      setInitState(resolvedInit);

      const savedMessages = CHATBOT_CONFIG.persistHistory ? loadMessages() : [];
      const initialMessages = buildInitialMessages(resolvedInit);

      setState(prev => {
        if (prev.messages.length > 0) {
          return prev;
        }

        if (savedMessages.length > 0) {
          return {
            ...prev,
            messages: savedMessages,
          };
        }

        return {
          ...prev,
          messages: initialMessages,
        };
      });

      setIsInitLoaded(true);
    };

    void loadInitialMessages();

    return () => {
      isMounted = false;
    };
  }, [authLocale, buildInitialMessages]);

  useEffect(() => {
    if (CHATBOT_CONFIG.persistHistory && state.messages.length > 0) {
      saveMessages(state.messages);
    }
  }, [state.messages]);

  useEffect(() => {
    saveChatBotOpenState(state.isOpen);
  }, [state.isOpen]);

  return useMemo(() => ({
    state,
    setState,
    initState,
    setInitState,
    isInitLoaded,
    isSessionReadyRef,
    resetSession,
  }), [initState, isInitLoaded, resetSession, state]);
};
